import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const bormesLesMimosasEn: ChauffeurPage = {
  slug: "bormes-les-mimosas",
  title: "Private chauffeur & VTC in Bormes-les-Mimosas",
  subtitle:
    "Private chauffeur in Bormes-les-Mimosas: flowered village, Fort de Brégançon, La Favière harbour and transfers to Toulon-Hyères and Nice airports.",
  keywords: [
    "private chauffeur bormes-les-mimosas",
    "vtc bormes",
    "bormes airport transfer",
    "chauffeur la favière",
  ],
  hero: {
    title: "Private chauffeur & VTC in Bormes-les-Mimosas",
    subtitle:
      "From the flowered hilltop village to La Favière harbour: a chauffeur for Cap Bénat and the whole shore, 7 days a week.",
    keywords: [
      "VTC Bormes-les-Mimosas",
      "La Favière chauffeur",
      "Fort de Brégançon",
      "Toulon-Hyères airport transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur & VTC in Bormes-les-Mimosas",
    badges: [
      "Toulon-Hyères airport ~35 min",
      "Village & shore",
      "FR/EN chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Bormes-les-Mimosas",
    items: [
      {
        title: "Hilltop village and seafront",
        description:
          "Steep lanes of the old village and the beaches of La Favière: the chauffeur links the two with no parking worry.",
      },
      {
        title: "Nearest airport",
        description:
          "Toulon-Hyères about thirty minutes away; Nice Côte d'Azur remains reachable in close to 1h40.",
      },
      {
        title: "Cap Bénat & Brégançon",
        description:
          "Access to Cap Bénat, Cabasson beach and the surroundings of Fort de Brégançon, the presidential residence.",
      },
      {
        title: "Rated 5.0/5",
        description:
          "Discreet, punctual chauffeurs based on the French Riviera, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Our chauffeur services in Bormes",
    services: [
      {
        title: "Toulon-Hyères airport transfer",
        text: "Around 30 to 35 min of driving, with flight tracking, to and from Toulon-Hyères airport.",
      },
      {
        title: "Nice airport transfer",
        text: "Long-distance link to Nice Côte d'Azur in close to 1h40, in a sedan or premium van.",
      },
      {
        title: "Village & La Favière harbour",
        text: "Shuttles between the old flowered village, the La Favière marina and the beaches.",
      },
      {
        title: "Toulon TGV station",
        text: "Transfer to Toulon station for TGV trains to Paris and regional connections.",
      },
      {
        title: "Hourly hire & long distance",
        text: "A chauffeur by the day across the Var, or runs to Saint-Tropez, Cannes, Nice and Italy.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Old village of Bormes",
        description: "Flowered hilltop village, lanes and small squares.",
      },
      {
        name: "La Favière harbour",
        description: "Marina and beaches of the maritime district.",
      },
      {
        name: "Cap Bénat & Cabasson",
        description: "The Cap Bénat estate and Cabasson beach.",
      },
      {
        name: "Fort de Brégançon",
        description: "Presidential residence, at the tip of the shore.",
      },
      {
        name: "Toulon-Hyères airport",
        description: "The nearest airport, around 35 min away.",
      },
      {
        name: "Le Lavandou",
        description: "Neighbouring resort and its twelve beaches.",
      },
    ],
  },
  generic: {
    heading: "Bormes-les-Mimosas, from the flowered village to Cap Bénat",
    subheading:
      "An old hilltop village and a discreet shore, linked by narrow roads a chauffeur knows well.",
    paragraphs: [
      "Bormes-les-Mimosas lives on two levels: the flowered old village, clinging to the hill with its steep lanes, and the seafront around La Favière harbour. Between the two, parking gets tricky from spring onwards, when the mimosa and then the summer draw visitors in. A private chauffeur shuttles door to door and drops you as close as possible, up top as much as down by the water.",
      "The Bormes shore leads to Cap Bénat, Cabasson beach and the surroundings of Fort de Brégançon, the summer residence of the Presidents of the Republic: confidential accesses the chauffeur takes without hesitation. To reach the region, Toulon-Hyères airport is the nearest at about thirty minutes; Nice Côte d'Azur is gained in close to 1h40, and Toulon's TGV station opens the Paris line.",
      "During a stay, hourly hire assigns a dedicated chauffeur to your days — the village, the beaches, dinners nearby, escapes to the Gulf of Saint-Tropez — with fixed fares set in advance and an instant quote.",
    ],
    bullets: [
      "Village ↔ shore shuttle",
      "Cap Bénat & Brégançon access",
      "Fixed fares & instant quote",
    ],
  },
  process: {
    heading: "Book a chauffeur in Bormes",
    steps: [
      "Tell us your flight, your train or your address (village or La Favière).",
      "We advise the vehicle to suit the group, the luggage and the journey.",
      "The chauffeur tracks the flight and adapts the departure to the coastal traffic.",
      "Direct transfer or hourly hire for the village and the beaches.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Bormes-les-Mimosas"),
    {
      q: "Which airport is closest to Bormes?",
      a: "Toulon-Hyères airport, about thirty minutes away, is the nearest. Nice Côte d'Azur, for international flights, is close to 1h40 by motorway.",
    },
    {
      q: "Do you serve the hilltop village despite the lanes?",
      a: "Yes. The heart of the village is partly pedestrian; we drop you as close as possible to the accesses and pick you up at the same spot, with no need to find a space on the slope.",
    },
    {
      q: "Do you handle transfers to Toulon TGV station?",
      a: "Yes, Toulon station links Paris by TGV and handles connections; we set the departure to your schedule, with a margin for the coastal traffic.",
    },
    {
      q: "How far in advance should I book?",
      a: "Plan ahead during the mimosa bloom in winter and in the height of summer; last-minute requests remain possible subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English and used to the international visitors of the village and the shore.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "A Mercedes E-Class or S-Class sedan, a premium 7-seater van for groups and luggage, plus SUV and VIP minibus to suit your needs.",
    },
    {
      q: "Do you cover long distances from Bormes?",
      a: "Yes: Saint-Tropez, Cannes, Nice, Monaco, Marseille and Italy, as well as runs from Cannes or Nice to Bormes, in a comfortable vehicle with a dedicated chauffeur.",
    },
  ],
  relatedLinks: [
    {
      label: "Book a chauffeur in Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    {
      label: "Book a chauffeur on the Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Cannes chauffeur rates", href: "/en/chauffeur/cannes" },
    { label: "Car service in Nice", href: "/en/chauffeur/nice" },
  ],
  cta: {
    title: "Your private chauffeur in Bormes-les-Mimosas",
    text: "Airport transfer, flowered village or La Favière harbour: book your chauffeur, quick reply 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book / request a quote",
  },
  seo: {
    title: "Private chauffeur in Bormes — car service & airport transfer",
    description:
      "Private chauffeur in Bormes-les-Mimosas: transfer to Toulon-Hyères (~35 min) and Nice airports, Toulon TGV station, Cap Bénat and La Favière.",
    canonical: "/en/chauffeur/bormes-les-mimosas",
  },
};

export default bormesLesMimosasEn;
